/**
 * Comprehensive Chart Components Library
 * Replicates all chart functionality from legacy dashboard
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  Filler,
} from 'chart.js';
import {
  Line,
  Bar,
  Doughnut,
  Pie,
  Radar,
  PolarArea,
} from 'react-chartjs-2';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  Filler
);

interface ChartWrapperProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  height?: number;
  actions?: React.ReactNode;
}

const ChartWrapper: React.FC<ChartWrapperProps> = ({ 
  title, 
  subtitle, 
  children, 
  height = 400,
  actions 
}) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {(title || actions) && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Box>
              {title && (
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {actions && (
              <Box>
                {actions}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ height }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
};

// Revenue Trend Line Chart
interface RevenueTrendChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      borderColor?: string;
      backgroundColor?: string;
      fill?: boolean;
    }>;
  };
  title?: string;
  height?: number;
}

export const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({ 
  data, 
  title = "Actual Revenue",
  height = 400 
}) => {
  const theme = useTheme();
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: $${context.parsed.y.toLocaleString()}`;
          }
        }
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Actual Revenue ($)'
        },
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return '$' + value.toLocaleString();
          }
        }
      }
    },
  };

  // Apply theme colors to datasets - configured for actual revenue display
  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: dataset.borderColor || theme.palette.primary.main,
      backgroundColor: dataset.backgroundColor || `${theme.palette.primary.main}15`,
      tension: 0.1, // Less smooth curve for actual data points
      pointRadius: 4, // Visible data points
      pointHoverRadius: 6,
      borderWidth: 2,
      fill: true, // Fill area for actual revenue visualization
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Line data={themedData} options={options} />
    </ChartWrapper>
  );
};

// AI Performance Bar Chart
interface AIPerformanceChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string[];
    }>;
  };
  title?: string;
  height?: number;
}

export const AIPerformanceChart: React.FC<AIPerformanceChartProps> = ({ 
  data, 
  title = "AI Performance Metrics",
  height = 400 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      }
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
      ],
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Bar data={themedData} options={options} />
    </ChartWrapper>
  );
};

// User Analytics Doughnut Chart
interface UserAnalyticsChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      data: number[];
      backgroundColor?: string[];
      borderColor?: string[];
    }>;
  };
  title?: string;
  height?: number;
}

export const UserAnalyticsChart: React.FC<UserAnalyticsChartProps> = ({ 
  data, 
  title = "User Distribution",
  height = 400 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      },
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
      ],
      borderColor: dataset.borderColor || theme.palette.background.paper,
      borderWidth: 2,
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Doughnut data={themedData} options={options} />
    </ChartWrapper>
  );
};

// System Health Radar Chart
interface SystemHealthChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string;
      borderColor?: string;
    }>;
  };
  title?: string;
  height?: number;
}

export const SystemHealthChart: React.FC<SystemHealthChartProps> = ({ 
  data, 
  title = "System Health",
  height = 400 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function(value: any) {
            return value + '%';
          }
        }
      }
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || `${theme.palette.primary.main}20`,
      borderColor: dataset.borderColor || theme.palette.primary.main,
      borderWidth: 2,
      pointBackgroundColor: theme.palette.primary.main,
      pointBorderColor: theme.palette.background.paper,
      pointHoverBackgroundColor: theme.palette.background.paper,
      pointHoverBorderColor: theme.palette.primary.main,
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Radar data={themedData} options={options} />
    </ChartWrapper>
  );
};

// Content Performance Stacked Bar Chart
interface ContentPerformanceChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string;
      stack?: string;
    }>;
  };
  title?: string;
  height?: number;
}

export const ContentPerformanceChart: React.FC<ContentPerformanceChartProps> = ({ 
  data, 
  title = "Content Performance",
  height = 400 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
      ][index % 5],
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Bar data={themedData} options={options} />
    </ChartWrapper>
  );
};

// Engagement Metrics Area Chart
interface EngagementChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      borderColor?: string;
      backgroundColor?: string;
      fill?: boolean;
    }>;
  };
  title?: string;
  height?: number;
}

export const EngagementChart: React.FC<EngagementChartProps> = ({ 
  data, 
  title = "User Engagement",
  height = 400 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Engagement (%)'
        },
        min: 0,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      }
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      fill: dataset.fill !== undefined ? dataset.fill : true,
      borderColor: dataset.borderColor || theme.palette.primary.main,
      backgroundColor: dataset.backgroundColor || `${theme.palette.primary.main}20`,
      tension: 0.4,
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Line data={themedData} options={options} />
    </ChartWrapper>
  );
};

// Quest Level Distribution Polar Chart
interface QuestDistributionChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      data: number[];
      backgroundColor?: string[];
    }>;
  };
  title?: string;
  height?: number;
}

export const QuestDistributionChart: React.FC<QuestDistributionChartProps> = ({ 
  data, 
  title = "Quest Level Distribution",
  height = 400 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
      },
    },
    scales: {
      r: {
        beginAtZero: true,
      }
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || [
        `${theme.palette.primary.main}80`,
        `${theme.palette.secondary.main}80`,
        `${theme.palette.success.main}80`,
        `${theme.palette.warning.main}80`,
        `${theme.palette.error.main}80`,
        `${theme.palette.info.main}80`,
        `${theme.palette.primary.dark}80`,
      ],
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <PolarArea data={themedData} options={options} />
    </ChartWrapper>
  );
};

// Real-time Status Chart
interface RealTimeStatusChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      borderColor?: string;
      backgroundColor?: string;
    }>;
  };
  title?: string;
  height?: number;
  refreshInterval?: number;
}

export const RealTimeStatusChart: React.FC<RealTimeStatusChartProps> = ({ 
  data, 
  title = "Real-time Status",
  height = 300,
  refreshInterval = 30000 
}) => {
  const theme = useTheme();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 750,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        display: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Value'
        }
      }
    },
  };

  const themedData = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      borderColor: dataset.borderColor || theme.palette.success.main,
      backgroundColor: dataset.backgroundColor || `${theme.palette.success.main}20`,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 5,
    }))
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Line data={themedData} options={options} />
    </ChartWrapper>
  );
};

// Communication Analytics Chart
interface CommunicationAnalyticsData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor?: string;
    borderWidth?: number;
  }[];
}

interface CommunicationAnalyticsChartProps {
  data: CommunicationAnalyticsData;
  title?: string;
  height?: number;
  type?: 'line' | 'bar' | 'doughnut';
}

export const CommunicationAnalyticsChart: React.FC<CommunicationAnalyticsChartProps> = ({ 
  data, 
  title = "Communication Analytics",
  height = 400,
  type = 'line'
}) => {
  const theme = useTheme();
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
        },
      },
      title: {
        display: !!title,
        text: title,
        color: theme.palette.text.primary,
        font: {
          size: 18,
          weight: 'bold',
        },
      },
    },
    scales: type !== 'doughnut' ? {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
    } : undefined,
  };

  return (
    <ChartWrapper title={title} height={height}>
      {type === 'line' && <Line data={data} options={options} />}
      {type === 'bar' && <Bar data={data} options={options} />}
      {type === 'doughnut' && <Doughnut data={data} options={options} />}
    </ChartWrapper>
  );
};

// Family Collaboration Chart
interface FamilyCollaborationData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor?: string;
  }[];
}

interface FamilyCollaborationChartProps {
  data: FamilyCollaborationData;
  title?: string;
  height?: number;
}

export const FamilyCollaborationChart: React.FC<FamilyCollaborationChartProps> = ({ 
  data, 
  title = "Family Collaboration",
  height = 400 
}) => {
  const theme = useTheme();
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
    },
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Bar data={data} options={options} />
    </ChartWrapper>
  );
};

// Monitoring Dashboard Chart
interface MonitoringData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    fill?: boolean;
  }[];
}

interface MonitoringChartProps {
  data: MonitoringData;
  title?: string;
  height?: number;
  type?: 'line' | 'area';
}

export const MonitoringChart: React.FC<MonitoringChartProps> = ({ 
  data, 
  title = "System Monitoring",
  height = 400,
  type = 'line'
}) => {
  const theme = useTheme();
  
  const chartData = {
    ...data,
    datasets: data.datasets.map(dataset => ({
      ...dataset,
      fill: type === 'area',
    }))
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
    },
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Line data={chartData} options={options} />
    </ChartWrapper>
  );
};

// AI Queue Monitoring Chart
interface AIQueueData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    tension?: number;
  }[];
}

interface AIQueueChartProps {
  data: AIQueueData;
  title?: string;
  height?: number;
}

export const AIQueueChart: React.FC<AIQueueChartProps> = ({ 
  data, 
  title = "AI Processing Queue",
  height = 400 
}) => {
  const theme = useTheme();
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
    },
  };

  return (
    <ChartWrapper title={title} height={height}>
      <Line data={data} options={options} />
    </ChartWrapper>
  );
};

export {
  ChartWrapper,
};

export default {
  RevenueTrendChart,
  AIPerformanceChart,
  UserAnalyticsChart,
  SystemHealthChart,
  ContentPerformanceChart,
  EngagementChart,
  QuestDistributionChart,
  RealTimeStatusChart,
  CommunicationAnalyticsChart,
  FamilyCollaborationChart,
  MonitoringChart,
  AIQueueChart,
  ChartWrapper,
};